import { Component, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef, Input } from '@angular/core';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { CategoryService } from 'src/app/services/category/category.service';
import { Subject, takeUntil, finalize } from 'rxjs';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { ProvidersService } from 'src/app/services/providers/providers.service';
import { query } from '@angular/animations';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-category-slider',
  templateUrl: './category-slider.component.html',
  styleUrls: ['./category-slider.component.scss'],
})
export class CategorySliderComponent implements AfterViewInit {
  @ViewChild('categoriesContainer') categoriesContainer!: ElementRef;
  @ViewChild('categoryGrid') categoryGrid!: ElementRef;
  @ViewChild('subcategoriesContainer') subcategoriesContainer!: ElementRef;
  
  @Input('showAllCategories') showAllCategories: boolean = true;
  @Input('showActiveCategory') showActiveCategory: boolean = false;
  @Input('showCategoryImage') showCategoryImage: boolean = true;
  @Input('showSubcategories') showSubcategories: boolean = false;

  @Input('categoriesFromExternalData') categoriesFromExternalData: Array<any> = [];

  @Input('selectedProvider') selectedProvider: string = '';
  @Input('selectedCategory') selectedCategory: string = '';
  @Input('selectedSubcategory') selectedSubcategory: string = '';

  // Icons
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faYoutube = faYoutube;
  loading = false;
  error = '';

  showCategoryButtons: boolean = true;
  showSubcategoryButtons: boolean = true;

  enableLeftCategoryButton = false;
  enableRightCategoryButton = true;
  enableLeftSubcategoryButton = false;
  enableRightSubcategoryButton = true;

  categories = [];
  subcategories = [];

  idProvider: string = '';

  constructor(
    private CategoryService: CategoryService,
    private router: Router,
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private providerService: ProvidersService,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    if(this.showAllCategories){
      this.getAllCategories();
    }
    else{
      this.categories = this.categoriesFromExternalData;
      if(this.showSubcategories){
        if(this.showActiveCategory && this.selectedCategory == '' && this.categories.length > 0){
          this.selectedCategory = this.categories[0]._id;
        }
        this.getSubcategoriesFromSelectedCategory();
      }
      this.showOrHideButtons();
    }

    this.route.queryParams.subscribe((params) => {
      this.idProvider = params['idProvider'] || '';
      this.getNameFromCategory(this.selectedCategory);
    });
  }

  ngAfterViewInit(): void {
    this.categoriesContainer.nativeElement.addEventListener('scroll', () => {
      this.checkScrollPosition('categories');
    });
    
    if(this.showSubcategories){
      this.subcategoriesContainer.nativeElement.addEventListener('scroll', () => {
        this.checkScrollPosition('subcategories');
      });  
    }
    
  }

  private checkScrollPosition(type: string): void {
    let container: HTMLElement | null = null;

    if (type === 'categories' && this.categoriesContainer) {
      container = this.categoriesContainer.nativeElement;
    } else if (this.subcategoriesContainer) {
      container = this.subcategoriesContainer.nativeElement;
    }

    if (!container) {
      return;
    }
  
    if (type === 'categories') {
      this.enableLeftCategoryButton = container.scrollLeft > 0;
    }
    else{
      this.enableLeftSubcategoryButton = container.scrollLeft > 0;
    }
    
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    
    if (container.scrollLeft >= maxScrollLeft) {
      if (type === 'categories') {
        this.enableRightCategoryButton = false;
      }
      else{
        this.enableRightSubcategoryButton = false;
      }
    }
    else{
      if (type === 'categories') {
        this.enableRightCategoryButton = true;
      }
      else{
        this.enableRightSubcategoryButton = true;
      }
    }
  }

  scrollLeft(type: string): void {
    const container = type === 'categories'
        ? this.categoriesContainer.nativeElement
        : this.subcategoriesContainer.nativeElement;
    
    container.scrollBy({
        left: -200,
        behavior: 'smooth',
    });

    if (container.scrollLeft > 0) {
        if (type === 'categories') {
            this.enableRightCategoryButton = true;
        } else {
            this.enableRightSubcategoryButton = true;
        }
    }
  }

  scrollRight(type: string): void {
    const container = type === 'categories'
          ? this.categoriesContainer.nativeElement
          : this.subcategoriesContainer.nativeElement;
    container.scrollBy({
      left: 200,
      behavior: 'smooth',
    });
  }

  isAllCategoriesActive(): boolean {
    return localStorage.getItem('selectedCategoryId') === '0';
  }

  private getAllCategories(): void {
    this.loading = true;
    this.CategoryService.getAllCategory(this.showSubcategories.toString())
    .pipe(
      finalize(() => {
        this.loading = false;
        this.CategoryService.setCategoriesLoaded(true);
      })
    )
    .subscribe({
      next: (data: any) => {
        this.categories = data.map((category) => ({
          ...category,
          active: category._id === localStorage.getItem('selectedCategoryId'),
        }));
        if(this.showActiveCategory && this.selectedCategory == '' && this.categories.length > 0){
          this.selectedCategory = this.categories[0]._id;
        }
        if(this.showSubcategories){
          this.getSubcategoriesFromSelectedCategory();
        }
        this.showOrHideButtons();

        this.cdRef.detectChanges();
        setTimeout(() => this.scrollToActiveCategory(), 0);
        this.getNameFromCategory(this.selectedCategory);
      },
      error: () => {
        this.error = 'Error al cargar las categorías';
      },
    });
  }

  private scrollToActiveCategory(): void {
    if(this.categoriesContainer?.nativeElement){
      const gridElement = this.categoriesContainer.nativeElement.querySelector('.categories-grid');
      const activeCategory = gridElement?.querySelector('.category-card.active');
    
      if (activeCategory) {
        activeCategory.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'center',
          scrollMode: 'if-needed'
        });
      }
    }
  }

  private showOrHideButtons(){
    this.cdRef.detectChanges();
    this.checkIfNeedsToHideButtons('categories');
    this.checkIfNeedsToHideButtons('subcategories');
  }

  private checkIfNeedsToHideButtons(type: string): void {
    const container = type === 'categories'
        ? this.categoriesContainer?.nativeElement
        : this.subcategoriesContainer?.nativeElement;
    
    if (!container) {
        return;
    }

    const gridElement = container.querySelector('.categories-grid');

    if (!gridElement) {
        return;
    }

    if (gridElement.clientWidth <= container.clientWidth) {
        if (type == 'categories') {
          this.showCategoryButtons = false;
        } else {
          this.showSubcategoryButtons = false;
        }
    }
    else{
      if (type == 'categories') {
        this.showCategoryButtons = true;
      } else {
        this.showSubcategoryButtons = true;
      }
    }
  }

  private getSubcategoriesFromSelectedCategory(isChangingCategory: boolean = false): void{
    const foundCategory = this.categories.find(category => category._id === this.selectedCategory);
    if(foundCategory){
      if(this.selectedProvider == ''){
        this.subcategories = foundCategory?.subcatList || [];
      }
      else{
        this.subcategories = foundCategory?.filteredSubcatList || [];
      }
      if(isChangingCategory){
        if(this.subcategories.length > 0){
          this.selectedSubcategory = this.subcategories[0]._id;
        }
      }

      this.cdRef.detectChanges();
      setTimeout(() => this.scrollToActiveSubcategory(), 0);
    }
  }

  private scrollToActiveSubcategory(){
    if(this.subcategoriesContainer?.nativeElement){
      const gridElement = this.subcategoriesContainer.nativeElement.querySelector('.categories-grid');
      const activeSubcategory = gridElement?.querySelector('.subcategory-card.active');
      if (activeSubcategory) {
        activeSubcategory.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'center',
          scrollMode: 'if-needed'
        });
      }
    }
  }

  selectCategory(selectedCategory: any): void {
    let queryParams: any = {};
    if(this.selectedProvider != ''){
      queryParams = {
        idProvider: this.selectedProvider
      }
    }

    this.selectedCategory = selectedCategory;
    this.getSubcategoriesFromSelectedCategory(true);

    queryParams = {
      ...queryParams,
      idCategory: selectedCategory
    };

    if(this.selectedSubcategory != '' && this.showSubcategories){
      queryParams = {
        ...queryParams,
        idSubcategory: this.selectedSubcategory
      };
    }
    else{
      queryParams = {
        ...queryParams
      };
    }

    this.showOrHideButtons();
    this.checkScrollPosition('categories');
    this.checkScrollPosition('subcategories');

    this.router.navigate(['/home/result'], {
      queryParams: queryParams,
    });
  }

  selectSubcategory(selectedSubcategory: any): void {
    let queryParams: any = {};
    if(this.selectedProvider != ''){
      queryParams = {
        idProvider: this.selectedProvider
      }
    }

    queryParams = {
      ...queryParams,
      ...{
        idCategory: this.selectedCategory,
        idSubcategory: selectedSubcategory
      }
    };

    this.selectedSubcategory = selectedSubcategory;

    this.router.navigate(['/home/result'], {
      queryParams: queryParams,
    });
  }

  getNameFromCategory(idCategory: string){
    if(this.idProvider == ''){
      const filteredCategory = this.categories.find(category => 
        category._id === idCategory
      );
      if(filteredCategory){
        this.titleService.setTitle(filteredCategory.name + ' | ' + 'plataforma');
      }
      else{
        this.titleService.setTitle('plataforma Tienda | La aplicación para los comerciantes del hueco');
      }
    }
  }
}
